import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {
    ActionCallbackConfig,
    SectionTitleConfig,
    TabListItem,
    EXTRA_DOCUMENT_TYPE,
} from '../shared.interface';
import { SharedService } from '../../e-met/services/shared.service';
import {
    AbstractControl,
    FormArray,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { DataService } from '../../e-met/services/data.service';
import { Observable, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

interface ButtonConfig extends ActionCallbackConfig {
    disabled: boolean;
    classes: string[];
}

interface DocumentFile {
    documentId: number;
    documentInput?: any;
    duration?: string;
    documentType?: string;
}

interface AdditionalCertificateMetaData {
    originalId: string;
    fileName?: string;
    documentType?: string;
    miscDocumentType?: string;
}

@Component({
    selector: 'app-additional-certificates-table',
    templateUrl: './additional-certificates-table.component.html',
    styleUrls: ['./additional-certificates-table.component.scss'],
})
export class AdditionalCertificatesTableComponent implements OnInit {
    @ViewChild('inputFile') myInputVariable: ElementRef;
    @Input() appId = null;
    @Input() citizen: TabListItem;
    @Input() extraDocumentTypes: EXTRA_DOCUMENT_TYPE;
    @Input() documents: DocumentFile[];
    sectionTitleConfig: SectionTitleConfig = {
        sectionTitle: 'Επιπλέον Δικαιολογητικά',
        imgPath: '/assets/images/list-icon.png',
        altText: '',
        required: false,
    };

    documentTypes: string[] = [EXTRA_DOCUMENT_TYPE.OTHER];
    columns = ['documentType', 'actions'];

    tableDataSource: MatTableDataSource<AbstractControl>;
    documentsFormArray: FormArray = new FormArray([]);

    buttonConfigMap: Map<string, ButtonConfig[]> = new Map();
    private selectedRow: { [key: string]: any } = null;
    private selectedFile: File = null;
    private subscriptions: Subscription[] = [];
    constructor(
        private sharedService: SharedService,
        private dataService: DataService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.tableDataSource = new MatTableDataSource(
            this.documentsFormArray.controls
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['documents'].currentValue) {
            const misc = this.documents.filter(
                (e) =>
                    e.documentType === 'MISC' &&
                    !this.documentExists(e.documentId.toString())
            );
            this.handleExistingDocuments(misc);
        }
    }

    addRow(
        documentId = this.sharedService.makeRandomDocId(6),
        documentType: string = null
    ): void {
        const fg = new FormGroup({
            documentType: new FormControl(
                { value: documentType, disabled: false },
                Validators.required
            ),
            documentId: new FormControl({ value: documentId, disabled: false }),
            tempId: new FormControl(documentId),
        });
        if (documentType) {
            // If addRow has been called for a file that already exists disable Document Type dropdown
            fg.get('documentType').disable();
        }
        this.documentsFormArray.push(fg);
        const newBtnConfigs = this.createButtonConfigs();
        if (documentType !== null) {
            // In case this is called for existing files we need to manually update the corresponding buttons
            const deleteBtn = newBtnConfigs.find((e) => e.label == 'Διαγραφή');
            deleteBtn.disabled = false;
            const openBtn = newBtnConfigs.find((e) => e.label == 'Προβολή');
            openBtn.disabled = false;
            const uploadBtn = newBtnConfigs.find(
                (e) => e.label == 'Μεταφόρτωση'
            );
            uploadBtn.disabled = true;
        }
        const subscription = this.handleFileRowChanges(fg);
        this.subscriptions.push(subscription);
        this.buttonConfigMap.set(documentId, newBtnConfigs);
        this.refreshTable();
    }

    private documentExists(id: string): boolean {
        return (
            this.documentsFormArray.controls.find(
                (e) => e.get('documentId').value === id
            ) != undefined
        );
    }

    private handleFileRowChanges(fg: FormGroup): Subscription {
        return fg.get('documentId').valueChanges.subscribe({
            next: (documentId) => {
                const newBtnConfigs = this.buttonConfigMap.get(fg.value.tempId);
                // Use value changes to determine what buttons should be enabled or not
                fg.get('documentType')?.disable();
                if (documentId) {
                    const deleteBtn = newBtnConfigs.find(
                        (e) => e.label == 'Διαγραφή'
                    );
                    deleteBtn.disabled = false;
                    const openBtn = newBtnConfigs.find(
                        (e) => e.label == 'Προβολή'
                    );
                    openBtn.disabled = false;
                    const uploadBtn = newBtnConfigs.find(
                        (e) => e.label == 'Μεταφόρτωση'
                    );
                    uploadBtn.disabled = true;
                }
                // Since we use the documentId in the ui to get the correct button configuration and now the id is changes we will swap
                // the contents in the map using the tempId
                this.buttonConfigMap.set(documentId, newBtnConfigs);
                // We have swapped contents and now we clear the entry related to tempId
                this.buttonConfigMap.delete(fg.value.tempId);
            },
        });
    }

    private createButtonConfigs(): ButtonConfig[] {
        return [
            {
                documentType: '',
                label: 'Προβολή',
                tip: 'Προβολή αρχείου',
                actionCallback: (data: DocumentFile) => {
                    this.dataService
                        .getSpecificDocument(this.appId, data.documentId)
                        .subscribe({
                            next: (response) => {
                                this.dataService.showFileInNewTab(
                                    response.body
                                );
                            },
                        });
                },
                disabled: true,
                classes: ['btn-primary'],
            },
            {
                documentType: '',
                label: 'Μεταφόρτωση',
                tip: 'Μεταφορτώστε ένα νέο πιστοποιητικό',
                actionCallback: (data) => {
                    this.selectedRow = data;
                    this.myInputVariable.nativeElement.click();
                },
                disabled: false,
                classes: ['btn-primary'],
            },
            {
                documentType: '',
                label: 'Διαγραφή',
                tip: 'Διαγραφή αρχείου',
                disabled: true,
                classes: ['btn-danger', 'btn-secondary', 'button-error'],
                actionCallback: (data) => {
                    this.dataService.openGenericDialog(
                        'Ενέργεια',
                        'Είστε σίγουροι πως θέλετε να διαγράψετε το συγκεκριμένο δικαιολογητικό τύπου: ' +
                            `(${data.documentType});`,
                        [
                            {
                                label: 'Ακύρωση',
                                actionClass: 'btn-secondary',
                                actionCallback: () => {},
                            },
                            {
                                label: 'Διαγραφή',
                                actionClass: 'btn-danger',
                                actionCallback: () => {
                                    this.selectedRow = data;
                                    this.deleteRow();
                                },
                            },
                        ]
                    );
                },
            },
        ];
    }

    handleFileInput(files: FileList): void {
        const fileToUpload = files.item(0);
        if (!fileToUpload) {
            return;
        }
        this.selectedFile = fileToUpload;
        if (!this.selectedRow.documentType) {
            this.dataService.onFailUpdateDocument(
                'Παρακαλώ επιλέξτε Τύπο Δικαιολογητικού'
            );
            const control = this.documentsFormArray.controls.find(
                (ctr) => ctr.value.tempId == this.selectedRow.tempId
            );
            control.get('documentType').markAsDirty();
            return;
        }
        this.uploadMiscFile();
    }

    private deleteRow(): void {
        this.dataService
            .deleteMiscFile(this.appId, this.selectedRow.documentId)
            .subscribe({
                next: () => {
                    const index = this.documentsFormArray.controls.findIndex(
                        (ctr) =>
                            ctr.value.documentId == this.selectedRow.documentId
                    );
                    this.documentsFormArray.removeAt(index);
                    this.tableDataSource.data =
                        this.documentsFormArray.controls;
                    this.cdr.detectChanges();
                    this.dataService.onSuccessUpdateDocument(
                        'To αρχείο διαγράφθηκε επιτυχώς'
                    );
                },
                error: (err) => {
                    const message = err.message || err.error?.message;
                    this.dataService.onFailUpdateDocument(message);
                },
            });
    }

    /**
     * handleExistingDocumens - Preprocess incoming documents so that they can be rendered on the page
     * @param {DocumentFile[]} docs
     */
    private handleExistingDocuments(docs: DocumentFile[]): void {
        for (const doc of docs) {
            if (!doc.documentInput) {
                continue;
            }
            const metaData: AdditionalCertificateMetaData = doc.documentInput;
            this.addRow(doc.documentId?.toString(), metaData.miscDocumentType);
        }
        this.refreshTable();
    }

    /**
     * handleNewDocs - Handle changes needed for documents that exist in the page but have no valid documentId yet
     * @param {DocumentFile[]} docs
     */
    private handleNewDocuments(docs: DocumentFile[]): void {
        for (const doc of docs) {
            if (!doc.documentInput) {
                continue;
            }
            const metaData:AdditionalCertificateMetaData = doc.documentInput;

            const existingController = this.documentsFormArray.controls.find(
                (e) => e.value.tempId === metaData.originalId
            );
            if (!existingController) continue;
            // Update current row with the appropriate ID. When this happends all the required changes for the row (enable/dissable of corresponding buttnos) are executeds
            existingController
                .get('documentId')
                ?.setValue(doc.documentId.toString());
        }
        this.refreshTable();
    }

    private async uploadMiscFile(): Promise<void> {
        const docParams : AdditionalCertificateMetaData = {
            miscDocumentType: this.selectedRow?.documentType,
            originalId: this.selectedRow?.tempId,
            fileName: this.selectedFile?.name
        };

        const apiObject$ = this.dataService
            .uploadMiscFile(
                this.appId,
                this.citizen.atakNumber,
                this.selectedFile,
                this.citizen.taxNumber,
                docParams
            )
            .pipe(
                switchMap(() => {
                    return this.dataService.citizenApplicationContracts$(
                        this.appId,
                        this.citizen.taxNumber,
                        this.citizen.atakNumber
                    ) as Observable<DocumentFile[]>;
                })
            );
        apiObject$.subscribe({
            next: (v) => {
                this.handleNewDocuments(
                    v.filter((e) => e.documentType === 'MISC')
                );
                this.dataService.onSuccessUpdateDocument(
                    `Το έγγραφο με όνομα ${this.selectedFile.name} μεταφορτώθηκε με επιτυχία.`,
                    `Επιτυχές ανέβασμα αρχείου`
                );
            },
            error: (e) => {
                const message = e.error;
                this.dataService.onFailUpdateDocument(message);
                this.myInputVariable.nativeElement.value = null;
            },
            complete: () => {},
        });
    }

    private refreshTable(): void {
        if (!this.tableDataSource) {
            this.tableDataSource = new MatTableDataSource([]);
        }
        this.tableDataSource.data = this.documentsFormArray.controls;
        this.cdr.detectChanges();
    }
    ngOnDestroy(): void {
        this.subscriptions.forEach((sub) => sub?.unsubscribe());
    }
}
